import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { LuX } from 'react-icons/lu';

import { cn } from 'utils/cn';

const SheetContext = React.createContext<{ close: () => void; onExit: () => void } | null>(null);

const useSheetContext = (caller: string) => {
    const context = React.useContext(SheetContext);
    if (!context) throw new Error(`${caller} must be used inside a Sheet`);
    return context;
};

type SheetProps = React.ComponentProps<typeof SheetPrimitive.Root> & {
    /**
     * Called once a closed sheet's content has unmounted, i.e. after its exit animation. Owners that
     * mount a sheet to show it use `<Sheet defaultOpen onExitComplete={unmount}>`. Only fires for
     * content rendered through SheetContent; render SheetContent unconditionally and drive the sheet
     * with open/defaultOpen, since remounting the content (e.g. a key change) is not an exit.
     */
    onExitComplete?: () => void;
};

/** Radix Dialog as a side sheet: onOpenChange keeps its Radix meaning, so a controlled owner can refuse a close. */
const Sheet = ({ open: openProp, defaultOpen = false, onOpenChange, onExitComplete, ...props }: SheetProps) => {
    const controlled = openProp !== undefined;
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
    const open = controlled ? openProp : uncontrolledOpen;
    const latest = React.useRef({ open, controlled, onOpenChange, onExitComplete });

    React.useLayoutEffect(() => {
        latest.current = { open, controlled, onOpenChange, onExitComplete };
    });

    const setOpen = React.useCallback((nextOpen: boolean) => {
        if (!latest.current.controlled) setUncontrolledOpen(nextOpen);
        latest.current.onOpenChange?.(nextOpen);
    }, []);

    const context = React.useMemo(
        () => ({
            close: () => setOpen(false),
            // Content also unmounts with its owner; unmounting while still open is not an exit.
            onExit: () => {
                if (!latest.current.open) latest.current.onExitComplete?.();
            },
        }),
        [setOpen],
    );

    return (
        <SheetContext.Provider value={context}>
            <SheetPrimitive.Root open={open} onOpenChange={setOpen} {...props} />
        </SheetContext.Provider>
    );
};

/** Requests a close of the enclosing sheet, like SheetClose: a controlled owner still decides via onOpenChange. */
const useSheetClose = () => useSheetContext('useSheetClose').close;

const ExitSentinel = () => {
    const { onExit } = useSheetContext('SheetContent');
    React.useEffect(() => onExit, [onExit]);
    return null;
};

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Overlay
        className={cn(
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80',
            className,
        )}
        {...props}
        ref={ref}
    />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
    'fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
    {
        variants: {
            side: {
                top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
                bottom: 'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
                left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
                right: 'inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
            },
        },
        defaultVariants: {
            side: 'right',
        },
    },
);

type SheetSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const sheetSizes: Record<SheetSize, string> = {
    sm: 'sm:max-w-[400px]',
    md: 'sm:max-w-[600px]',
    lg: 'sm:max-w-[700px]',
    xl: 'sm:max-w-[800px]',
    '2xl': 'sm:max-w-[900px]',
};

type SheetContentProps = React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> &
    VariantProps<typeof sheetVariants> &
    ({ layout?: undefined; size?: never } | { layout: 'scroll-body'; size: SheetSize });

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
    ({ side = 'right', layout, size, className, children, ...props }, ref) => (
        <SheetPortal>
            <SheetOverlay />
            <SheetPrimitive.Content
                ref={ref}
                className={cn(
                    sheetVariants({ side }),
                    layout === 'scroll-body' && 'flex w-full flex-col gap-0 p-0',
                    layout === 'scroll-body' && sheetSizes[size],
                    className,
                )}
                {...props}
            >
                <ExitSentinel />
                {children}
                <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
                    <LuX className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                </SheetPrimitive.Close>
            </SheetPrimitive.Content>
        </SheetPortal>
    ),
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
    className,
    variant,
    ...props
}: React.HTMLAttributes<HTMLDivElement> & { variant?: 'pinned' }) => (
    <div
        className={cn(
            'flex flex-col space-y-2 text-center sm:text-left',
            variant === 'pinned' && 'shrink-0 border-b px-8 py-4 pr-16',
            className,
        )}
        {...props}
    />
);
SheetHeader.displayName = 'SheetHeader';

/**
 * Scrolling region for sheets with a persistent header and footer: use SheetContent with
 * layout="scroll-body" and a size, and render the header, any filters, SheetBody and the footer as
 * siblings so only SheetBody scrolls. Use variant="pinned" on SheetHeader and, for details sheets,
 * variant="full-width" on SheetFooter. Forms spanning body and footer need `flex min-h-0 flex-1 flex-col`.
 * Keep a SheetTitle for accessibility (`sr-only` for untitled sheets), and pass
 * `aria-describedby={undefined}` to SheetContent when omitting SheetDescription.
 * Form sheets should use FormSheet, which applies the standard header, body and footer.
 */
const SheetBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain', className)} {...props} />
    ),
);
SheetBody.displayName = 'SheetBody';

const SheetFooter = ({
    className,
    variant,
    ...props
}: React.HTMLAttributes<HTMLDivElement> & { variant?: 'full-width' }) => (
    <div
        className={cn(
            variant === 'full-width'
                ? 'block shrink-0'
                : 'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
            className,
        )}
        {...props}
    />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Title ref={ref} className={cn('text-foreground text-lg font-semibold', className)} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Description ref={ref} className={cn('text-muted-foreground text-sm', className)} {...props} />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
    type SheetSize,
    Sheet,
    SheetBody,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetOverlay,
    SheetTitle,
    SheetTrigger,
    useSheetClose,
};
